import React from 'react';
import { Helmet } from 'react-helmet-async';
import Layout from '@/components/Layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { 
  ThermometerSun, 
  Wind, 
  Home, 
  Calculator, 
  Shield, 
  AlertTriangle,
  TrendingUp,
  Phone,
  Mail
} from 'lucide-react';
import { Link } from 'react-router-dom';

const RoofVentilationGuide: React.FC = () => {
  const currentDate = new Date().toLocaleDateString();
  
  return (
    <Layout>
      <Helmet>
        <title>The Complete Roof Ventilation Guide | Carolina Trophy Roofs</title>
        <meta name="description" content="Expert guide to roof ventilation systems, components, and installation. Learn about ridge vents, soffit vents, and proper attic ventilation for North Carolina homes." />
        <meta name="keywords" content="roof ventilation, attic ventilation, ridge vents, soffit vents, ventilation guide, North Carolina roofing" />
        <link rel="canonical" href="https://www.carolinatrophyroofs.com/resources/roof-ventilation-guide" />
        
        {/* Open Graph */}
        <meta property="og:title" content="The Complete Roof Ventilation Guide | Carolina Trophy Roofs" />
        <meta property="og:description" content="Expert guide to roof ventilation systems, components, and installation. Learn about ridge vents, soffit vents, and proper attic ventilation." />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://www.carolinatrophyroofs.com/lovable-uploads/9fe71b94-4ce7-4ea1-9432-d90056b06d8a.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="The Complete Roof Ventilation Guide | Carolina Trophy Roofs" />
        <meta name="twitter:description" content="Expert guide to roof ventilation systems, components, and installation." />
        <meta name="twitter:image" content="https://www.carolinatrophyroofs.com/lovable-uploads/9fe71b94-4ce7-4ea1-9432-d90056b06d8a.png" />
        
        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "The Complete Roof Ventilation Guide",
            "description": "Expert guide to roof ventilation systems, components, and installation",
            "image": "https://www.carolinatrophyroofs.com/lovable-uploads/9fe71b94-4ce7-4ea1-9432-d90056b06d8a.png",
            "author": {
              "@type": "Organization",
              "name": "Carolina Trophy Roofs"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Carolina Trophy Roofs",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.carolinatrophyroofs.com/lovable-uploads/2f5a66d3-a9ff-41b4-888b-9060533e361f.png"
              }
            },
            "datePublished": currentDate,
            "dateModified": currentDate
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-r from-brand-charcoal-gray to-brand-charcoal-gray/80 text-white py-20 md:py-28 overflow-hidden"
        style={{
          backgroundImage: `url('/lovable-uploads/5f3827e5-0c99-43d2-80dc-04b1857cd807.png')`,
          backgroundSize: '200px 200px',
          backgroundRepeat: 'repeat',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-brand-charcoal-gray/90"></div>
        
        <div className="relative container mx-auto px-4 z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="secondary" className="mb-4 bg-brand-trophy-gold/20 text-brand-trophy-gold border-brand-trophy-gold/30">
              Technical Resource Guide
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-brand-trophy-gold drop-shadow-lg">
              The Complete Roof Ventilation Guide
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white max-w-3xl mx-auto drop-shadow-md">
              Components, Materials, and Performance Metrics for North Carolina Homes
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-brand-safety-orange hover:bg-brand-safety-orange/90 text-white font-semibold" asChild>
                <Link to="/contact-us">Get Professional Assessment</Link>
              </Button>
              <Button size="lg" variant="outline" className="border-white text-black hover:bg-white hover:text-brand-charcoal-gray" asChild>
                <Link to="/book-inspection">Schedule Inspection</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Abstract */}
          <Alert className="mb-12 border-orange-200 bg-orange-50">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-sm">
              <strong>Why This Guide Matters:</strong> Property owners searching for ventilation solutions typically arrive here after experiencing excessive energy bills ($300-800/month in summer), premature shingle failure, moisture damage, or insurance claims denied due to "improper ventilation."
            </AlertDescription>
          </Alert>

          <Card className="mb-12">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Home className="h-5 w-5" />
                Abstract
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">
                This comprehensive analysis examines residential and commercial roof ventilation systems, encompassing passive and active technologies, material specifications, installation methodologies, and performance metrics. Drawing from manufacturer specifications, building science research, and field performance data, this guide serves as an authoritative reference for understanding ventilation system selection, implementation, and optimization in varying climate conditions.
              </p>
            </CardContent>
          </Card>

          {/* Fundamental Ventilation Science */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-12 text-center">Fundamental Ventilation Science</h2>
            
            {/* Custom card for airflow diagram - wide format */}
            <div className="mb-12">
              <div className="bg-white rounded-xl shadow-lg border overflow-hidden">
                <div className="p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                  <h3 className="text-2xl font-bold text-center text-blue-900">Ventilation Airflow Principles</h3>
                  <p className="text-center text-blue-700 mt-2">Ridge Vents, Soffit Vents, and Natural Air Circulation</p>
                </div>
                <div className="p-8">
                  <div className="flex justify-center mb-8">
                    <img 
                      src="/lovable-uploads/b1cf81ec-19ad-4cab-bff6-1e860a1a1909.png" 
                      alt="Technical diagram showing proper roof ventilation airflow patterns with ridge vents and soffit vents"
                      className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md"
                      style={{ maxHeight: '600px' }}
                    />
                  </div>
                  <div className="grid md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-blue-50 p-6 rounded-lg">
                      <h4 className="font-semibold text-blue-800 mb-3">Stack Effect</h4>
                      <p className="text-sm text-blue-700">
                        Warm air rises naturally, creating pressure differentials that drive airflow through intake and exhaust vents.
                      </p>
                    </div>
                    <div className="bg-green-50 p-6 rounded-lg">
                      <h4 className="font-semibold text-green-800 mb-3">Wind Pressure</h4>
                      <p className="text-sm text-green-700">
                        External wind creates positive and negative pressure zones, enhancing natural ventilation effectiveness.
                      </p>
                    </div>
                    <div className="bg-purple-50 p-6 rounded-lg">
                      <h4 className="font-semibold text-purple-800 mb-3">Thermal Dynamics</h4>
                      <p className="text-sm text-purple-700">
                        Temperature differentials between attic and outdoor air amplify ventilation airflow rates.
                      </p>
                    </div>
                  </div>
                  
                  <Card className="bg-blue-50 border-blue-200">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-blue-800">
                        <Calculator className="h-5 w-5" />
                        The 300:1 Rule Explained
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="mb-4 text-blue-900">
                        IRC Section R806.2 specifies 1 ft² Net Free Area per 300 ft² attic space, but research indicates this minimum often proves insufficient for North Carolina's climate:
                      </p>
                      <div className="grid sm:grid-cols-3 gap-4">
                        <div className="bg-red-100 p-4 rounded-lg border border-red-200">
                          <h4 className="font-semibold text-red-800">Hot-Humid Zones</h4>
                          <p className="text-sm text-red-700">1:250 recommended</p>
                          <p className="text-xs text-red-600 mt-1">Coastal NC regions</p>
                        </div>
                        <div className="bg-yellow-100 p-4 rounded-lg border border-yellow-200">
                          <h4 className="font-semibold text-yellow-800">Mixed-Humid Zones</h4>
                          <p className="text-sm text-yellow-700">1:300 acceptable</p>
                          <p className="text-xs text-yellow-600 mt-1">Central NC regions</p>
                        </div>
                        <div className="bg-blue-100 p-4 rounded-lg border border-blue-200">
                          <h4 className="font-semibold text-blue-800">Cold Zones</h4>
                          <p className="text-sm text-blue-700">1:150 with vapor barriers</p>
                          <p className="text-xs text-blue-600 mt-1">Mountain NC regions</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </section>

          {/* Types of Ventilation Systems */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-12 text-center">Types of Ventilation Systems</h2>
            
            {/* Custom card for ventilation types - tall format matching image ratio */}
            <div className="mb-8">
              <div className="bg-white rounded-xl shadow-lg border overflow-hidden">
                <div className="p-6 border-b bg-gradient-to-r from-green-50 to-emerald-50">
                  <h3 className="text-2xl font-bold text-center text-green-900">Types of Ventilation Systems</h3>
                  <p className="text-center text-green-700 mt-2">Comprehensive Overview of All Vent Types</p>
                </div>
                <div className="p-8">
                  <div className="flex justify-center mb-8">
                    <img 
                      src="/lovable-uploads/273ca339-85d8-4c4e-bb39-059da01f52c5.png" 
                      alt="Types of roof ventilation components including ridge vents, exhaust vents, roof flashings, roof vents, and gable vents with technical diagrams"
                      className="w-full max-w-3xl h-auto object-contain rounded-lg shadow-md"
                      style={{ maxHeight: '900px' }}
                    />
                  </div>
                  
                  <div className="grid lg:grid-cols-2 gap-8">
                    <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                      <h4 className="text-xl font-bold text-blue-800 mb-4">Passive Systems</h4>
                      <div className="space-y-3">
                        <div className="bg-white p-4 rounded border">
                          <h5 className="font-semibold text-blue-700">Ridge Vents</h5>
                          <p className="text-sm text-gray-600">Continuous exhaust along roof peak</p>
                          <p className="text-xs text-blue-600">NFA: 10-18 sq in/linear foot</p>
                        </div>
                        <div className="bg-white p-4 rounded border">
                          <h5 className="font-semibold text-blue-700">Soffit Vents</h5>
                          <p className="text-sm text-gray-600">Intake vents under eaves</p>
                          <p className="text-xs text-blue-600">NFA: 4-9 sq in/linear foot</p>
                        </div>
                        <div className="bg-white p-4 rounded border">
                          <h5 className="font-semibold text-blue-700">Static Vents</h5>
                          <p className="text-sm text-gray-600">Individual roof-mounted exhausts</p>
                          <p className="text-xs text-blue-600">NFA: 50-144 sq in/unit</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-orange-50 p-6 rounded-lg border border-orange-200">
                      <h4 className="text-xl font-bold text-orange-800 mb-4">Active Systems</h4>
                      <div className="space-y-3">
                        <div className="bg-white p-4 rounded border">
                          <h5 className="font-semibold text-orange-700">Powered Exhausts</h5>
                          <p className="text-sm text-gray-600">Electric or solar-powered fans</p>
                          <p className="text-xs text-orange-600">CFM: 1,200-1,600/unit</p>
                        </div>
                        <div className="bg-white p-4 rounded border">
                          <h5 className="font-semibold text-orange-700">Turbine Vents</h5>
                          <p className="text-sm text-gray-600">Wind-driven rotating exhausts</p>
                          <p className="text-xs text-orange-600">CFM: 300-500/unit</p>
                        </div>
                        <div className="bg-white p-4 rounded border">
                          <h5 className="font-semibold text-orange-700">Whole House Fans</h5>
                          <p className="text-sm text-gray-600">Large-capacity interior fans</p>
                          <p className="text-xs text-orange-600">CFM: 4,000-7,000/unit</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Component Analysis */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-12 text-center">Comprehensive Component Analysis</h2>
            
            {/* Custom card for detailed technical diagram - wide format */}
            <div className="mb-8">
              <div className="bg-white rounded-xl shadow-lg border overflow-hidden">
                <div className="p-6 border-b bg-gradient-to-r from-purple-50 to-violet-50">
                  <h3 className="text-2xl font-bold text-center text-purple-900">Detailed Attic Ventilation Analysis</h3>
                  <p className="text-center text-purple-700 mt-2">Technical Specifications and Calculations</p>
                </div>
                <div className="p-8">
                  <div className="flex justify-center mb-8">
                    <img 
                      src="/lovable-uploads/debc6964-cfb8-46a1-bddc-ea3129fae822.png" 
                      alt="Comprehensive attic ventilation technical diagram with detailed explanations of all components and airflow patterns"
                      className="w-full max-w-6xl h-auto object-contain rounded-lg shadow-md"
                      style={{ maxHeight: '700px' }}
                    />
                  </div>
                  
                  <div className="grid md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                      <h4 className="font-semibold text-blue-800 mb-3">ShingleVent II</h4>
                      <ul className="text-sm space-y-2">
                        <li>• <strong>NFA:</strong> 18 sq in/linear foot</li>
                        <li>• <strong>Wind rating:</strong> 110 mph</li>
                        <li>• <strong>Material:</strong> Copolymer</li>
                        <li>• <strong>Cost:</strong> $3.50-4.50/linear foot</li>
                        <li>• <strong>Installation:</strong> Cut 1.5" slot, nail every 12"</li>
                        <li>• <strong>Warranty:</strong> 20 years</li>
                      </ul>
                    </div>
                    <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                      <h4 className="font-semibold text-green-800 mb-3">RidgeMaster Plus</h4>
                      <ul className="text-sm space-y-2">
                        <li>• <strong>NFA:</strong> 12.5 sq in/linear foot</li>
                        <li>• <strong>Wind rating:</strong> 130 mph</li>
                        <li>• <strong>Material:</strong> Polypropylene</li>
                        <li>• <strong>Cost:</strong> $2.75-3.75/linear foot</li>
                        <li>• <strong>Installation:</strong> Cut 2" slot, nail pattern specific</li>
                        <li>• <strong>Warranty:</strong> 15 years</li>
                      </ul>
                    </div>
                    <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
                      <h4 className="font-semibold text-purple-800 mb-3">Cobra Rigid Vent 3</h4>
                      <ul className="text-sm space-y-2">
                        <li>• <strong>NFA:</strong> 10 sq in/linear foot</li>
                        <li>• <strong>Wind rating:</strong> 180 mph (Miami-Dade)</li>
                        <li>• <strong>Material:</strong> Rigid plastic</li>
                        <li>• <strong>Cost:</strong> $4.00-5.00/linear foot</li>
                        <li>• <strong>Installation:</strong> Hurricane-rated fastening</li>
                        <li>• <strong>Warranty:</strong> 25 years</li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h4 className="font-semibold text-gray-800 mb-3">Installation Best Practices</h4>
                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <h5 className="font-medium text-gray-700 mb-2">Ridge Vent Requirements:</h5>
                        <ul className="space-y-1 text-gray-600">
                          <li>• Minimum 1.5" slot width</li>
                          <li>• 12" clearance from gable ends</li>
                          <li>• Proper shingle overlap</li>
                          <li>• Weather-resistant fasteners</li>
                        </ul>
                      </div>
                      <div>
                        <h5 className="font-medium text-gray-700 mb-2">Performance Considerations:</h5>
                        <ul className="space-y-1 text-gray-600">
                          <li>• Wind uplift resistance</li>
                          <li>• Snow infiltration prevention</li>
                          <li>• Insect screening effectiveness</li>
                          <li>• Aesthetic integration</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Soffit Ventilation Components</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold mb-4 text-lg">Continuous Soffit Vents</h4>
                    <div className="space-y-4">
                      <div className="bg-gray-50 p-4 rounded-lg border">
                        <p className="font-medium">Aluminum Strip Vents</p>
                        <p className="text-sm mt-1">NFA: 6-9 sq in/linear foot</p>
                        <p className="text-sm">Cost: $1.50-2.50/linear foot</p>
                        <p className="text-xs text-gray-600 mt-1">Installation: Cut soffit, secure with screws</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg border">
                        <p className="font-medium">Vinyl Vented Panels</p>
                        <p className="text-sm mt-1">NFA: 4-7 sq in/linear foot</p>
                        <p className="text-sm">Cost: $8-12/panel (12 sq ft)</p>
                        <p className="text-xs text-gray-600 mt-1">Installation: Replace solid panels entirely</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-4 text-lg">Individual Soffit Vents</h4>
                    <div className="space-y-4">
                      <div className="bg-gray-50 p-4 rounded-lg border">
                        <p className="font-medium">Round Aluminum (2-4" diameter)</p>
                        <p className="text-sm mt-1">NFA: 16-50 sq in/unit</p>
                        <p className="text-sm">Cost: $2-4/unit</p>
                        <p className="text-xs text-gray-600 mt-1">Installation: Hole saw, caulk seal</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg border">
                        <p className="font-medium">Rectangular Under-Eave</p>
                        <p className="text-sm mt-1">NFA: 36-65 sq in/unit</p>
                        <p className="text-sm">Cost: $4-8/unit</p>
                        <p className="text-xs text-gray-600 mt-1">Installation: Jigsaw cut, flange mount</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Ventilation Calculations */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-12 text-center">Ventilation Area Calculations</h2>
            
            {/* Custom card for calculation diagram - landscape format */}
            <div className="mb-8">
              <div className="bg-white rounded-xl shadow-lg border overflow-hidden">
                <div className="p-6 border-b bg-gradient-to-r from-orange-50 to-red-50">
                  <h3 className="text-2xl font-bold text-center text-orange-900">Recommended Ventilation Areas</h3>
                  <p className="text-center text-orange-700 mt-2">Engineering Standards and Code Requirements</p>
                </div>
                <div className="p-8">
                  <div className="flex justify-center mb-8">
                    <img 
                      src="/lovable-uploads/d9534c78-2e7e-4f0d-98bc-02ffcf070f90.png" 
                      alt="Technical diagram showing recommended attic ventilation area calculations with upper and lower vent requirements"
                      className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md"
                      style={{ maxHeight: '600px' }}
                    />
                  </div>
                  
                  <div className="grid lg:grid-cols-2 gap-8">
                    <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                      <h4 className="text-xl font-bold text-blue-800 mb-4">Calculation Formula</h4>
                      <div className="space-y-3">
                        <div className="bg-white p-4 rounded border">
                          <h5 className="font-semibold text-blue-700">Basic Rule (IRC)</h5>
                          <p className="text-sm text-gray-600">Attic Area ÷ 300 = Total NFA Required</p>
                          <p className="text-xs text-blue-600">Example: 1,500 sq ft ÷ 300 = 5 sq ft NFA</p>
                        </div>
                        <div className="bg-white p-4 rounded border">
                          <h5 className="font-semibold text-blue-700">Split Requirement</h5>
                          <p className="text-sm text-gray-600">50% intake (soffit) / 50% exhaust (ridge)</p>
                          <p className="text-xs text-blue-600">Example: 2.5 sq ft soffit + 2.5 sq ft ridge</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-orange-50 p-6 rounded-lg border border-orange-200">
                      <h4 className="text-xl font-bold text-orange-800 mb-4">Climate Adjustments</h4>
                      <div className="space-y-3">
                        <div className="bg-white p-4 rounded border">
                          <h5 className="font-semibold text-orange-700">Hot Climate Zones</h5>
                          <p className="text-sm text-gray-600">Increase by 20-30% (NC Coastal)</p>
                          <p className="text-xs text-orange-600">1,500 sq ft = 6.5 sq ft NFA total</p>
                        </div>
                        <div className="bg-white p-4 rounded border">
                          <h5 className="font-semibold text-orange-700">Steep Roof Adjustment</h5>
                          <p className="text-sm text-gray-600">Add 10% for slopes over 8:12</p>
                          <p className="text-xs text-orange-600">Enhanced stack effect requires more intake</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Performance Metrics */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-12 text-center">Performance Metrics and ROI Analysis</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="h-fit">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Energy Savings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                      <h4 className="font-semibold text-green-800 mb-3">Cooling Season Impact</h4>
                      <ul className="text-sm text-green-700 space-y-2">
                        <li>• Attic temperature reduction: 20-40°F</li>
                        <li>• Ceiling heat flux reduction: 30-45%</li>
                        <li>• AC runtime reduction: 10-15%</li>
                        <li>• Dollar savings: $150-400/season</li>
                        <li>• Peak demand reduction: 15-25%</li>
                      </ul>
                    </div>
                    <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                      <h4 className="font-semibold text-blue-800 mb-3">Heating Season Benefits</h4>
                      <ul className="text-sm text-blue-700 space-y-2">
                        <li>• Moisture reduction: 15-25% RH</li>
                        <li>• Ice dam prevention value: $5,000-50,000</li>
                        <li>• Insulation performance: 100% R-value maintained</li>
                        <li>• Condensation elimination: 90% reduction</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="h-fit">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Shingle Life Extension
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="mb-6 text-sm">Temperature reduction impact on shingle life:</p>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-4 bg-red-50 rounded-lg border border-red-200">
                      <span className="font-medium">10°F reduction</span>
                      <Badge variant="secondary" className="bg-red-100">20% life extension</Badge>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                      <span className="font-medium">20°F reduction</span>
                      <Badge variant="secondary" className="bg-yellow-100">40% life extension</Badge>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg border border-green-200">
                      <span className="font-medium">30°F reduction</span>
                      <Badge variant="secondary" className="bg-green-100">60% life extension</Badge>
                    </div>
                  </div>
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <h5 className="font-medium text-gray-800 mb-2">Economic Impact:</h5>
                    <p className="text-sm text-gray-600">
                      Proper ventilation can extend a $15,000 roof replacement by 5-10 years, 
                      representing $3,000-6,000 in avoided costs.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Installation Best Practices */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-12 text-center">Installation Best Practices</h2>
            
            <div className="grid lg:grid-cols-3 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle>Ridge Vent Installation</CardTitle>
                </CardHeader>
                <CardContent>
                  <ol className="list-decimal list-inside space-y-3 text-sm">
                    <li>Slot width: 1.5" minimum, 2" maximum</li>
                    <li>End termination: 12" from gable</li>
                    <li>Shingle overlap: Per manufacturer</li>
                    <li>Nail pattern: 1.5" from edges, 12" O.C.</li>
                    <li>Sealant: Not recommended at joints</li>
                    <li>Weather stripping: Install per code</li>
                  </ol>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Soffit Vent Protection</CardTitle>
                </CardHeader>
                <CardContent>
                  <div>
                    <p className="font-medium mb-3">Insulation Baffles:</p>
                    <ul className="text-sm space-y-2">
                      <li>• Material: Polystyrene or cardboard</li>
                      <li>• Extension: 4" minimum above insulation</li>
                      <li>• Airspace: 1" minimum maintained</li>
                      <li>• Attachment: Staples or friction fit</li>
                      <li>• Coverage: Every rafter bay</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Power Vent Wiring</CardTitle>
                </CardHeader>
                <CardContent>
                  <div>
                    <p className="font-medium mb-3">Electrical Requirements:</p>
                    <ul className="text-sm space-y-2">
                      <li>• Dedicated 15-amp circuit recommended</li>
                      <li>• GFCI protection required</li>
                      <li>• Junction box: Accessible location</li>
                      <li>• Thermostat placement: Away from direct sun</li>
                      <li>• Solar options: Battery backup systems</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Professional Services CTA */}
          <section 
            className="mb-16 py-16 bg-brand-charcoal-gray text-white rounded-lg overflow-hidden relative"
            style={{
              backgroundImage: `url('/lovable-uploads/05712f55-6608-4d58-b71e-fc4dd31ec82a.png')`,
              backgroundSize: '200px 200px',
              backgroundRepeat: 'repeat',
              backgroundPosition: 'center'
            }}
          >
            <div className="absolute inset-0 bg-brand-charcoal-gray/90"></div>
            
            <div className="relative z-10 container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-3xl font-bold mb-6 text-brand-trophy-gold">Professional Ventilation Services</h2>
                <p className="text-xl mb-8">
                  Carolina Trophy Roofs provides comprehensive ventilation services including thermal imaging diagnostics, airflow calculations, professional installation, and maintenance programs throughout North Carolina.
                </p>
                
                <div className="grid sm:grid-cols-2 gap-6 mb-8">
                  <div className="bg-white/10 p-6 rounded-lg">
                    <Phone className="h-8 w-8 mb-4 mx-auto" />
                    <h3 className="font-semibold mb-2">Call for Consultation</h3>
                    <p className="text-sm mb-4">Speak with our ventilation experts</p>
                    <Button variant="secondary" size="lg" className="w-full bg-brand-safety-orange hover:bg-brand-safety-orange/90 text-white font-semibold">
                      (252) 302-2003
                    </Button>
                  </div>
                  <div className="bg-white/10 p-6 rounded-lg">
                    <Mail className="h-8 w-8 mb-4 mx-auto" />
                    <h3 className="font-semibold mb-2">Contact Us</h3>
                    <p className="text-sm mb-4">Get in touch with our roofing experts</p>
                    <Button variant="secondary" size="lg" className="w-full bg-brand-safety-orange hover:bg-brand-safety-orange/90 text-white font-semibold" asChild>
                      <Link to="/contact">Contact Us Today</Link>
                    </Button>
                  </div>
                </div>

                <div className="flex flex-wrap justify-center gap-4">
                  <Button variant="outline" className="border-white text-black hover:bg-white hover:text-brand-charcoal-gray" asChild>
                    <Link to="/residential-roofing">Residential Services</Link>
                  </Button>
                  <Button variant="outline" className="border-white text-black hover:bg-white hover:text-brand-charcoal-gray" asChild>
                    <Link to="/commercial-roofing">Commercial Services</Link>
                  </Button>
                  <Button variant="outline" className="border-white text-black hover:bg-white hover:text-brand-charcoal-gray" asChild>
                    <Link to="/storm-damage">Storm Damage</Link>
                  </Button>
                  <Button variant="outline" className="border-white text-black hover:bg-white hover:text-brand-charcoal-gray" asChild>
                    <Link to="/resources">More Resources</Link>
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Related Resources */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-12 text-center">Related Resources</h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">
                    <Link to="/resources/insurance-claim-process" className="hover:text-primary">
                      Insurance Claim Process
                    </Link>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Learn how proper ventilation documentation supports insurance claims and prevents coverage denials.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">
                    <Link to="/resources/roofing-materials-faq" className="hover:text-primary">
                      Roofing Materials FAQ
                    </Link>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Understand how different roofing materials affect ventilation requirements and performance.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">
                    <Link to="/resources/manufacturer-warranties" className="hover:text-primary">
                      Manufacturer Warranties
                    </Link>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Discover how proper ventilation maintains manufacturer warranties and extends material life.
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Technical Footer */}
          <section className="text-center py-8 border-t">
            <p className="text-sm text-muted-foreground mb-4">
              <em>This technical guide represents current industry best practices as of {currentDate}. Consult local codes and manufacturer specifications for project-specific requirements.</em>
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-xs text-muted-foreground">
              <Link to="/about" className="hover:text-primary">About Carolina Trophy Roofs</Link>
              <Link to="/frequently-asked-questions" className="hover:text-primary">Frequently Asked Questions</Link>
              <Link to="/financing" className="hover:text-primary">Financing Options</Link>
              <Link to="/careers" className="hover:text-primary">Join Our Team</Link>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default RoofVentilationGuide;
